import React from 'react';
import PropTypes from 'prop-types';
import { Button, Stack, Typography, CircularProgress } from '@mui/joy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const LoadMoreButton = ({
  onLoadMore,
  loading,
  hasMore,
  currentCount,
  totalCount,
  itemType = 'items',
}) => {
  const remainingCount = totalCount ? totalCount - currentCount : null;

  return (
    <Stack spacing={2} sx={{ alignItems: 'center', py: 4 }} role="region" aria-live="polite">
      <Typography
        sx={{
          color: "var(--ce-dark-secondary)",
          fontSize: "18px",
          lineHeight: "26px",
          textAlign: "center",
        }}
        id="load-more-status"
      >
        Showing {currentCount} of {totalCount || 'many'} {itemType}
        {remainingCount && ` (${remainingCount} more available)`}
      </Typography>

      {hasMore && (
        <Button
          variant="outlined"
          size="lg"
          onClick={onLoadMore}
          disabled={loading}
          loading={loading}
          loadingPosition="start"
          startDecorator={loading ? <CircularProgress size="sm" /> : <ExpandMoreIcon />}
          aria-describedby="load-more-status"
          sx={{
            minWidth: 160,
            minHeight: "48px",
            px: 3,
            fontSize: "16px",
            fontWeight: 600,
            borderRadius: "6px",
            borderColor: "#97c3f0",
            color: "#0b6bcb",
            backgroundColor: "white",
            transition: "box-shadow 0.15s ease, border-color 0.15s ease",
            "&:hover": {
              backgroundColor: "#f0f8ff",
              borderColor: "#0b6bcb",
              boxShadow: "0 2px 8px rgba(11,107,203,0.2)",
            },
            "&:disabled": { opacity: 0.6 },
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        >
          {loading ? `Loading...` : `Load more`}
        </Button>
      )}

      {loading && (
        <Typography
          sx={{ color: "rgba(255,255,255,0.6)", fontSize: "14px" }}
          role="status"
          aria-live="polite"
        >
          Loading additional {itemType}, please wait...
        </Typography>
      )}
    </Stack>
  );
};

LoadMoreButton.propTypes = {
  onLoadMore: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  hasMore: PropTypes.bool.isRequired,
  currentCount: PropTypes.number.isRequired,
  totalCount: PropTypes.number,
  itemType: PropTypes.string,
};

LoadMoreButton.defaultProps = {
  loading: false,
  totalCount: null,
  itemType: 'items',
};

export default LoadMoreButton;
